import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";
import { MyselfDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import type { MatchMode } from "./design-codec";
import { ModeSelect } from "./ModeSelect";
import { AccessRow, PersonAdder } from "./MatchModals";
import { roleToAxes } from "./match-access";
import type { AccessRowDraft, AssignableRole } from "./match-access";
import type { MatchAccessInput } from "./use-match-editor";

export interface NewMatchDraft {
  matchMode: MatchMode;
  name: string;
  description: string;
  access: MatchAccessInput[];
}

// Deferred-create dialog (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4, revised): the whole creation happens in one
// popup — mode + name/notes/access — and the Match is only created on submit
// (via onCreate), so no orphan row exists before the user commits and matchMode
// is set once at creation (the column is immutable). Renders through the shared
// Dialog (native <dialog>: focus trap + modal semantics + Escape). Mounted only
// while open by its callers, so `open` is always true here.
export function NewMatchModal({
  onCreate,
  onClose,
}: {
  onCreate: (draft: NewMatchDraft) => Promise<void>;
  onClose: () => void;
}) {
  const intl = useIntl();
  const { data: myselfData } = useQuery(MyselfDocument);
  const owner = myselfData?.myself;
  const ownerName =
    [(owner?.name?.given ?? []).filter(Boolean).join(" "), owner?.name?.family]
      .filter(Boolean)
      .join(" ") || "?";

  const [mode, setMode] = useState<MatchMode>("OPTIMIZE");
  const defaultName = intl.formatMessage({
    id: "match.new.defaultName",
    defaultMessage: "New match",
  });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [access, setAccess] = useState<AccessRowDraft[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setRowRole(personId: string, nextRole: AssignableRole) {
    setAccess((rows) => rows.map((r) => (r.personId === personId ? { ...r, role: nextRole } : r)));
  }
  function removeRow(personId: string) {
    setAccess((rows) => rows.filter((r) => r.personId !== personId));
  }
  function addPerson(p: { id: string; formattedName: string }) {
    setAccess((rows) =>
      rows.some((r) => r.personId === p.id)
        ? rows
        : [...rows, { personId: p.id, formattedName: p.formattedName, role: "editor" }],
    );
  }

  async function create() {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      await onCreate({
        matchMode: mode,
        name: name.trim() || defaultName,
        description: description.trim(),
        access: access.map((r) => ({ personId: r.personId, ...roleToAxes(r.role) })),
      });
      // The caller navigates on success; unmount handles cleanup.
    } catch (e) {
      setError(userErrorMessage(intl, e));
      setCreating(false);
    }
  }

  const excludeIds = useMemo(
    () => [owner?.id, ...access.map((r) => r.personId)].filter((s): s is string => !!s),
    [access, owner?.id],
  );

  return (
    <Dialog
      open
      id="new-match-modal"
      onClose={onClose}
      preventClose
      maxWidth="lg"
      title={<FormattedMessage id="match.new.title" defaultMessage="Neues Match erstellen" />}
    >
      <div className="space-y-6">
        <div>
          <div className="mb-3 text-smycles-meta text-ink-muted">
            <FormattedMessage
              id="match.modePicker.title"
              defaultMessage="What question should this match answer?"
            />
          </div>
          <ModeSelect idPrefix="new-match-mode" value={mode} onChange={setMode} disabled={creating} />
        </div>

        <div className="space-y-4 border-t border-ink/5 pt-5">
          <div>
            <label htmlFor="new-match-name" className="smycles-field-label mb-1 block">
              <FormattedMessage id="match.matchNameLabel" defaultMessage="Match-Name" />
            </label>
            <input
              id="new-match-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={defaultName}
              className="bg-page w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
            />
          </div>

          <div>
            <label htmlFor="new-match-notes" className="smycles-field-label mb-1 block">
              <FormattedMessage id="match.modal.matchNotes" defaultMessage="Notizen zum Match" />
            </label>
            <textarea
              id="new-match-notes"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder={intl.formatMessage({
                id: "match.modal.matchNotesPlaceholder",
                defaultMessage:
                  "Interne Notizen, Kontext oder offene Fragen – nicht die Projektbeschreibung.",
              })}
              className="bg-page w-full resize-none rounded-md px-3 py-2 text-smycles-body leading-relaxed ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
            />
          </div>

          <div>
            <div className="smycles-field-label mb-2">
              <FormattedMessage id="match.new.accessLabel" defaultMessage="Zugriff" />
            </div>
            <div className="mb-2 max-h-56 divide-y divide-ink/5 overflow-y-auto">
              {owner && (
                <div className="flex items-center justify-between gap-3 py-2 text-smycles-meta">
                  <span className="min-w-0 truncate font-medium">{ownerName}</span>
                  <span className="text-ink-muted">
                    <FormattedMessage id="match.modal.owner" defaultMessage="Eigentümer:in" />
                  </span>
                </div>
              )}
              {access.map((row) => (
                <AccessRow
                  key={row.personId}
                  row={row}
                  canEdit
                  onChange={(nextRole) => setRowRole(row.personId, nextRole)}
                  onRemove={() => removeRow(row.personId)}
                />
              ))}
            </div>
            <PersonAdder idPrefix="new-match-access" onAdd={addPerson} excludeIds={excludeIds} />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">{error}</div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button id="new-match-cancel" variant="secondary" size="compact" onClick={onClose} disabled={creating}>
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </Button>
          <Button id="new-match-create" variant="primary" size="compact" onClick={() => void create()} disabled={creating}>
            {creating ? (
              <FormattedMessage id="match.new.creating" defaultMessage="Erstellt …" />
            ) : (
              <FormattedMessage id="match.new.create" defaultMessage="Erstellen" />
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
